import { useCallback, useEffect, useState } from 'react';
import { loadMetaPixel, removeMetaPixel } from '@/lib/meta-pixel';

const STORAGE_KEY = 'velko-cookie-consent';
const GA_ID = 'G-J3KGT2K3P1';

type ConsentStatus = 'accepted' | 'rejected' | null;

function getStoredConsent(): ConsentStatus {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'accepted' || value === 'rejected') return value;
  } catch {
    // localStorage unavailable
  }
  return null;
}

function loadGA4() {
  if (document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  gtag('js', new Date());
  gtag('config', GA_ID);
}

function removeGA4() {
  const script = document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`);
  if (script) script.remove();

  // Clear GA cookies
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name?.startsWith('_ga') || name?.startsWith('_gid')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentStatus>(getStoredConsent);

  useEffect(() => {
    if (consent === 'accepted') {
      loadGA4();
      loadMetaPixel();
    } else if (consent === 'rejected') {
      removeGA4();
      removeMetaPixel();
    }
  }, [consent]);

  const acceptCookies = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
  }, []);

  const rejectCookies = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'rejected');
    setConsent('rejected');
  }, []);

  return { consent, acceptCookies, rejectCookies };
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
